import { call, put, takeLatest, debounce } from '@spcc/core'
import { extractErrorMessage } from '@spcc/core'
import type { PayloadAction } from '@reduxjs/toolkit'
import { scanActions } from './scan.slice'
import { shipmentsApi } from '../infrastructure'
import type { ResourceSuggestion, OrderScanResponse } from '../api/scan-types'

function* scanOrderSaga(action: PayloadAction<string>) {
  try {
    const response: OrderScanResponse = yield call(shipmentsApi.scanOrder, action.payload)
    yield put(scanActions.scanOrderSuccess(response))
  } catch (error: any) {
    const message = error.response?.status === 404
      ? 'orders.errors.notFound'
      : extractErrorMessage(error) || 'queue.errors.scanFailed'
    yield put(scanActions.scanOrderFailure(message))
  }
}

function* fetchSuggestionsSaga(action: PayloadAction<{ orderNumber: string; licensePlate?: string; driverPhone?: string }>) {
  try {
    const { orderNumber, licensePlate, driverPhone } = action.payload
    // Only fetch if we have enough input
    if (!orderNumber || (!licensePlate && !driverPhone)) {
      yield put(scanActions.fetchSuggestionsSuccess([]))
      return
    }

    const suggestions: ResourceSuggestion[] = yield call(shipmentsApi.suggestResources, orderNumber, {
      licensePlate: licensePlate || null,
      driverPhone: driverPhone || null,
    })
    yield put(scanActions.fetchSuggestionsSuccess(suggestions))
  } catch (error) {
    // Silent fail for suggestions
    yield put(scanActions.fetchSuggestionsFailure())
  }
}

function* assignResourceSaga(
  action: PayloadAction<{ orderNumber: string; resource: any; cityId?: string | null }>
) {
  try {
    const { orderNumber, resource, cityId } = action.payload
    yield call(shipmentsApi.assignResource, orderNumber, resource, cityId)
    
    yield put(scanActions.assignResourceSuccess())
    
    // Refresh the order scan to show updated status
    yield put(scanActions.scanOrder(orderNumber))
  } catch (error: any) {
    const message = extractErrorMessage(error) || 'weighbridgeOps.errors.assignResourceFailed'
    yield put(scanActions.assignResourceFailure(message))
  }
}

export function* scanSaga() {
  yield takeLatest(scanActions.scanOrder.type, scanOrderSaga)
  yield debounce(300, scanActions.fetchSuggestions.type, fetchSuggestionsSaga)
  yield takeLatest(scanActions.assignResource.type, assignResourceSaga)
}
